/**
 * 🔴 **화면이 깨졌는데, 어디서만 알고 무엇인지 모른다.**
 *
 * 2026-08-31 운영 기록: `app_error` 가 `/rally` 에서 8건, 나머지 6건. 그게 전부였다.
 * 종류(`ChunkLoadError` 인지 `TypeError` 인지)는 GA4 로만 보냈는데 **GA4 를 안 켰다** —
 * 우리 집계가 유일한 지표인데 거기엔 화면 이름만 남는다. 8건을 보고도 무엇을 고쳐야 할지
 * 알 수가 없다. 새로고침이면 풀리는 것과 진짜 버그는 할 일이 완전히 다르다.
 *
 * ## 왜 종류는 보내도 되나(불변식 1)
 * 오류 **원문(메시지)** 에는 닉네임·주소가 섞일 수 있어서 안 보낸다. 종류는 자바스크립트가
 * 붙이는 **클래스 이름**(`TypeError`·`ChunkLoadError`)이라 사람 이야기가 들어갈 자리가 없다.
 * 그래도 혹시 모르니 여기서 **영문자만** 남기고 잘라 낸다 — 메시지가 새어 들어올 길을 막는다.
 */

/** 종류에서 남길 수 있는 것 — 영문자만, 짧게. 메시지가 섞여도 여기서 다 떨어진다. */
export function errorKindTag(kind: unknown): string {
  const s = typeof kind === "string" ? kind.replace(/[^A-Za-z]/g, "") : "";
  return s.slice(0, 24) || "Error";
}

/**
 * 📍 **터진 자리** — 파일 이름만. `Book-DKAoqCFU.js:1` → `Book`.
 *
 * 🔴 예전엔 이 값을 만들어 놓고 **GA4 로만 보냈다**(꺼져 있다). 그래서 운영 기록에는
 * `other|TypeError` 만 남았고, 어느 코드가 터졌는지는 **아무도, 나중에도 알 수 없었다**
 * (2026-09-01 실측: 그런 기록이 2건 있었는데 끝내 못 찾았다).
 *
 * 프로덕션 조각 이름이 읽을 수 있는 모양이라(`Book-…js` · `createGame-…js`) 이것만으로도
 * 「그림책이냐 체험공간이냐」가 바로 갈린다. **판마다 바뀌는 해시는 떼어 낸다** —
 * 안 떼면 배포할 때마다 새 열쇠가 생겨 같은 버그가 흩어진다.
 *
 * 사람 이야기가 들어갈 자리가 없다(우리 조각 이름이다). 그래도 글자를 좁히고 잘라 낸다.
 */
export function errorSiteTag(at: unknown): string {
  if (typeof at !== "string" || !at) return "";
  const file = at.split(":")[0]!.split("/").pop() ?? "";
  const noExt = file.replace(/\.(tsx?|jsx?|mjs)$/, "");
  const noHash = noExt.replace(/-[A-Za-z0-9_-]{8}$/, ""); // Vite 조각 해시
  return noHash.replace(/[^A-Za-z0-9._-]/g, "").slice(0, 24);
}

/**
 * 집계 열쇠 — `화면|종류|자리`. 옛 기록은 `화면|종류` 또는 `화면` 뿐이라 **앞이 같다**.
 * 자리를 모르면 두 칸으로 둔다 — 없는 정보를 `unknown` 같은 말로 지어내지 않는다.
 */
export function errorStatKey(where: unknown, kind: unknown, at?: unknown): string {
  const w = typeof where === "string" && where ? where : "other";
  const site = errorSiteTag(at);
  return site ? `${w}|${errorKindTag(kind)}|${site}` : `${w}|${errorKindTag(kind)}`;
}

/** 사람이 읽을 설명 — 종류마다 **할 일이 다르다**. 모르는 종류는 이름 그대로 보여 준다. */
const KIND_SAY: Record<string, string> = {
  ChunkLoadError: "새 판이 올라와 옛 조각을 못 찾음(새로고침이면 풀림)",
  TypeError: "없는 값을 읽으려 함 — 진짜 버그일 가능성이 높음",
  ReferenceError: "없는 이름을 부름 — 진짜 버그",
  SyntaxError: "파일이 깨져서 내려옴(배포·캐시 의심)",
  SecurityError: "브라우저가 막음(카메라·저장소 권한 등)",
  NotAllowedError: "사용자가 권한을 거부함 — 버그가 아닐 수 있음",
  AbortError: "중간에 취소됨 — 화면을 떠난 것일 수 있음",
};

/** 저장된 열쇠(`/rally|TypeError|Book` · 옛 `/rally|ChunkLoadError` · 더 옛 `/rally`)를 화면 글로. */
export function errorStatSay(key: string): {
  where: string;
  kind?: string;
  hint?: string;
  site?: string;
} {
  const [where = "", kind, site] = key.split("|");
  if (kind === undefined) return { where: where || "(모름)" };
  return { where: where || "(모름)", kind, hint: KIND_SAY[kind], site: site || undefined };
}
